import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  Target,
  Loader2,
  ClipboardCheck,
  Sparkles,
  Download,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import api from '../services/api';

const PREFILL_KEY = 'governwell_healthcheck_prefill';

const MATURITY_STYLES = {
  Emerging: 'bg-red-500 text-white',
  Developing: 'bg-[#f5a623] text-black',
  Established: 'bg-lime text-black',
  Mature: 'bg-ink text-lime',
};

const Stat = ({ percent }) => {
  let bar = 'bg-red-500';
  if (percent >= 80) bar = 'bg-ink';
  else if (percent >= 60) bar = 'bg-lime';
  else if (percent >= 40) bar = 'bg-[#f5a623]';
  return (
    <div className="w-full h-4 border border-ink/10 rounded-lg bg-white">
      <div className={`h-full ${bar}`} style={{ width: `${percent}%` }} />
    </div>
  );
};

const HealthCheck = () => {
  const navigate = useNavigate();
  const [stage, setStage] = useState('intro'); // intro | quiz | optin | results
  const [questionnaire, setQuestionnaire] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [responses, setResponses] = useState({});
  const [catIndex, setCatIndex] = useState(0);
  const [contact, setContact] = useState({
    full_name: '',
    organisation_name: '',
    role: '',
    email: '',
  });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [outcome, setOutcome] = useState(null);

  useEffect(() => {
    let active = true;
    api
      .getHealthCheckQuestions()
      .then((data) => {
        if (active) setQuestionnaire(data);
      })
      .catch((err) => {
        if (active) setLoadError(err.message || 'Could not load the health check.');
      });
    return () => {
      active = false;
    };
  }, []);

  const categories = questionnaire?.categories || [];
  const answerOptions = questionnaire?.answer_options || [];
  const totalQuestions = useMemo(
    () => categories.reduce((sum, c) => sum + c.questions.length, 0),
    [categories]
  );
  const answeredCount = Object.keys(responses).length;
  const currentCategory = categories[catIndex];
  const categoryComplete =
    currentCategory && currentCategory.questions.every((q) => responses[q.id]);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email.trim());
  const optinValid =
    contact.full_name.trim() &&
    contact.organisation_name.trim() &&
    emailValid &&
    consent;

  const updateContact = (field) => (e) =>
    setContact((prev) => ({ ...prev, [field]: e.target.value }));

  const setAnswer = (qid, value) =>
    setResponses((prev) => ({ ...prev, [qid]: value }));

  const goNextCategory = () => {
    if (catIndex < categories.length - 1) {
      setCatIndex((i) => i + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setSubmitError('');
      setStage('optin');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goPrevCategory = () => {
    if (catIndex > 0) {
      setCatIndex((i) => i - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setStage('intro');
    }
  };

  const handleSubmit = async () => {
    setSubmitError('');
    setSubmitting(true);
    try {
      const cleanContact = Object.fromEntries(
        Object.entries(contact).filter(([, v]) => v && v.trim())
      );
      const result = await api.submitHealthCheck({ ...cleanContact, consent, responses });
      setOutcome(result);
      setStage('results');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const startFullReview = () => {
    try {
      localStorage.setItem(
        PREFILL_KEY,
        JSON.stringify({
          contact,
          responses,
          results: outcome?.results || null,
          savedAt: new Date().toISOString(),
        })
      );
    } catch (e) {
      /* non-blocking */
    }
    navigate('/dashboard');
  };

  return (
    <div className="brand-theme min-h-screen overflow-x-hidden selection:bg-ink selection:text-lime">
      <Header />

      <main className="max-w-4xl mx-auto px-6 py-12 md:py-16">
        {loadError && (
          <div className="border border-ink/10 rounded-lg shadow-saas bg-red-500 text-white p-6 font-bold">
            {loadError}
          </div>
        )}

        {!loadError && !questionnaire && (
          <div className="flex items-center gap-3 font-bold text-xl py-20 justify-center">
            <Loader2 className="w-6 h-6 animate-spin" /> Loading health check…
          </div>
        )}

        {/* INTRO */}
        {questionnaire && stage === 'intro' && (
          <div>
            <div className="inline-flex items-center gap-2 bg-lime border border-ink/10 rounded-lg px-4 py-1 font-bold uppercase text-sm mb-6">
              <ClipboardCheck className="w-4 h-4" /> Free diagnostic
            </div>
            <h1 className="font-bold tracking-tight text-5xl md:text-6xl mb-6">
              Governance Health Check
            </h1>
            <p className="text-xl md:text-2xl font-medium mb-8 max-w-2xl">
              Answer {totalQuestions} quick questions across {categories.length} areas
              of governance. Get an instant maturity score, your top strengths and the
              gaps worth prioritising — in about 5 minutes.
            </p>

            <div className="grid sm:grid-cols-3 gap-4 mb-10">
              {categories.slice(0, 6).map((c) => (
                <div key={c.id} className="border border-ink/10 rounded-lg bg-white p-4 font-bold">
                  {c.name}
                </div>
              ))}
            </div>

            <button
              onClick={() => setStage('quiz')}
              className="bg-ink text-white border border-ink/10 rounded-lg shadow-saas px-8 py-4 font-bold text-xl flex items-center gap-3"
            >
              Start the health check <ArrowRight className="w-6 h-6" />
            </button>
          </div>
        )}

        {/* QUIZ */}
        {questionnaire && stage === 'quiz' && currentCategory && (
          <div>
            <div className="flex items-center justify-between mb-2 font-bold uppercase text-sm">
              <span>
                Category {catIndex + 1} of {categories.length}
              </span>
              <span>
                {answeredCount}/{totalQuestions} answered
              </span>
            </div>
            <div className="w-full h-4 border border-ink/10 rounded-lg bg-white mb-8">
              <div
                className="h-full bg-lime"
                style={{
                  width: `${totalQuestions ? (answeredCount / totalQuestions) * 100 : 0}%`,
                }}
              />
            </div>

            <h2 className="font-bold tracking-tight text-3xl md:text-4xl mb-8">
              {currentCategory.name}
            </h2>

            <div className="space-y-6">
              {currentCategory.questions.map((q, qi) => (
                <div key={q.id} className="border border-ink/10 rounded-lg shadow-saas bg-white p-5">
                  <p className="font-bold text-lg mb-4">
                    {qi + 1}. {q.text}
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {answerOptions.map((opt) => {
                      const selected = responses[q.id] === opt.value;
                      return (
                        <button
                          key={opt.value}
                          onClick={() => setAnswer(q.id, opt.value)}
                          className={`border border-ink/10 rounded-lg px-3 py-3 font-bold transition-all ${
                            selected
                              ? 'bg-ink text-lime'
                              : 'bg-cream hover:bg-lime'
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {submitError && (
              <div className="border border-ink/10 rounded-lg bg-red-500 text-white p-4 font-bold mt-8">
                {submitError}
              </div>
            )}

            <div className="flex items-center justify-between mt-10">
              <button
                onClick={goPrevCategory}
                className="border border-ink/10 rounded-lg bg-white px-6 py-3 font-bold flex items-center gap-2"
              >
                <ArrowLeft className="w-5 h-5" /> Back
              </button>
              <button
                onClick={goNextCategory}
                disabled={!categoryComplete || submitting}
                className={`border border-ink/10 rounded-lg px-8 py-3 font-bold flex items-center gap-2 ${
                  categoryComplete && !submitting
                    ? 'bg-ink text-white shadow-saas'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              >
                {catIndex < categories.length - 1 ? 'Next category' : 'Continue'}
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* OPT-IN / LEAD CAPTURE */}
        {questionnaire && stage === 'optin' && (
          <div className="max-w-xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-lime border border-ink/10 rounded-lg px-4 py-1 font-bold uppercase text-sm mb-6">
              <ClipboardCheck className="w-4 h-4" /> One last step
            </div>
            <h1 className="font-bold tracking-tight text-4xl md:text-5xl mb-4">
              See your results
            </h1>
            <p className="text-lg font-medium mb-8">
              Your answers are scored and ready. Tell us where to send them and we'll
              unlock your personalised governance health report.
            </p>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (optinValid && !submitting) handleSubmit();
              }}
              className="space-y-5"
            >
              <div className="space-y-2">
                <label htmlFor="oc-name" className="font-bold uppercase text-sm tracking-wide">
                  Full name <span className="text-red-500">*</span>
                </label>
                <input
                  id="oc-name"
                  type="text"
                  value={contact.full_name}
                  onChange={updateContact('full_name')}
                  placeholder="Jane Trustee"
                  className="w-full h-12 px-4 bg-white border border-ink/10 rounded-lg font-medium focus:outline-none focus:bg-lime/20 placeholder:text-ink/40"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="oc-org" className="font-bold uppercase text-sm tracking-wide">
                  Organisation name <span className="text-red-500">*</span>
                </label>
                <input
                  id="oc-org"
                  type="text"
                  value={contact.organisation_name}
                  onChange={updateContact('organisation_name')}
                  placeholder="Your charity or company"
                  className="w-full h-12 px-4 bg-white border border-ink/10 rounded-lg font-medium focus:outline-none focus:bg-lime/20 placeholder:text-ink/40"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="oc-role" className="font-bold uppercase text-sm tracking-wide">
                  Your role
                </label>
                <input
                  id="oc-role"
                  type="text"
                  value={contact.role}
                  onChange={updateContact('role')}
                  placeholder="e.g. Chair, CEO, Trustee"
                  className="w-full h-12 px-4 bg-white border border-ink/10 rounded-lg font-medium focus:outline-none focus:bg-lime/20 placeholder:text-ink/40"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="oc-email" className="font-bold uppercase text-sm tracking-wide">
                  Email address <span className="text-red-500">*</span>
                </label>
                <input
                  id="oc-email"
                  type="email"
                  value={contact.email}
                  onChange={updateContact('email')}
                  placeholder="you@organisation.org.uk"
                  className="w-full h-12 px-4 bg-white border border-ink/10 rounded-lg font-medium focus:outline-none focus:bg-lime/20 placeholder:text-ink/40"
                />
              </div>

              <label className="flex items-start gap-3 font-medium cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 w-5 h-5 accent-black flex-shrink-0"
                />
                <span>
                  Send me my results and occasional governance insights from GovernWell.{' '}
                  <span className="text-red-500">*</span>
                </span>
              </label>

              {submitError && (
                <div className="border border-ink/10 rounded-lg bg-red-500 text-white p-4 font-bold">
                  {submitError}
                </div>
              )}

              <div className="flex items-center justify-between pt-2">
                <button
                  type="button"
                  onClick={() => setStage('quiz')}
                  className="border border-ink/10 rounded-lg bg-white px-6 py-3 font-bold flex items-center gap-2"
                >
                  <ArrowLeft className="w-5 h-5" /> Back
                </button>
                <button
                  type="submit"
                  disabled={!optinValid || submitting}
                  className={`border border-ink/10 rounded-lg px-8 py-3 font-bold flex items-center gap-2 ${
                    optinValid && !submitting
                      ? 'bg-ink text-white shadow-saas'
                      : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" /> Scoring…
                    </>
                  ) : (
                    <>
                      Get my results <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>

              <p className="text-xs font-medium text-ink/60 pt-2">
                We'll only use your details to send your results and relevant governance
                insights. You can unsubscribe at any time.
              </p>
            </form>
          </div>
        )}

        {/* RESULTS */}
        {stage === 'results' && outcome && (
          <ResultsView
            outcome={outcome}
            contact={contact}
            onStartFullReview={startFullReview}
            onDownloadPdf={() => api.downloadHealthCheckReport(outcome.lead_id)}
          />
        )}
      </main>

      <Footer />
    </div>
  );
};

const ResultsView = ({ outcome, contact, onStartFullReview, onDownloadPdf }) => {
  const { results, ai_summary } = outcome;
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const maturityClass =
    MATURITY_STYLES[results.maturity_level] || 'bg-lime text-black';

  const handleDownload = async () => {
    setDownloadError('');
    setDownloading(true);
    try {
      await onDownloadPdf();
    } catch (err) {
      setDownloadError(err.message || 'Could not generate the PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="inline-flex items-center gap-2 bg-lime border border-ink/10 rounded-lg px-4 py-1 font-bold uppercase text-sm">
          Results for {contact.organisation_name || 'your organisation'}
        </div>
        <button
          onClick={handleDownload}
          disabled={downloading}
          className={`border border-ink/10 rounded-lg px-5 py-2.5 font-bold flex items-center gap-2 ${
            downloading
              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
              : 'bg-ink text-white shadow-saas'
          }`}
        >
          {downloading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" /> Preparing…
            </>
          ) : (
            <>
              <Download className="w-5 h-5" /> Download PDF
            </>
          )}
        </button>
      </div>
      {downloadError && (
        <div className="border border-ink/10 rounded-lg bg-red-500 text-white p-4 font-bold mb-6">
          {downloadError}
        </div>
      )}
      <h1 className="font-bold tracking-tight text-4xl md:text-5xl mb-8">
        Your Governance Health
      </h1>

      {/* Headline score */}
      <div className="border border-ink/10 rounded-lg shadow-saas bg-white p-8 mb-8 flex flex-col md:flex-row items-center gap-8">
        <div className="text-center">
          <div className="text-7xl font-extrabold leading-none">
            {results.overall_percent}%
          </div>
          <div className="font-bold uppercase text-sm mt-2">Overall score</div>
        </div>
        <div className="flex-1 w-full">
          <span
            className={`inline-block ${maturityClass} border border-ink/10 rounded-lg px-4 py-1 font-bold uppercase mb-3`}
          >
            {results.maturity_level}
          </span>
          <p className="font-medium text-lg">{results.maturity_blurb}</p>
        </div>
      </div>

      {/* Category breakdown */}
      <h2 className="font-bold tracking-tight text-2xl mb-4">By category</h2>
      <div className="space-y-4 mb-10">
        {results.category_scores.map((c) => (
          <div key={c.id} className="border border-ink/10 rounded-lg bg-white p-4">
            <div className="flex items-center justify-between font-bold mb-2">
              <span>{c.name}</span>
              <span>{c.percent}%</span>
            </div>
            <Stat percent={c.percent} />
          </div>
        ))}
      </div>

      {/* Strengths + gaps */}
      <div className="grid md:grid-cols-2 gap-6 mb-10">
        <div className="border border-ink/10 rounded-lg shadow-saas bg-white p-6">
          <h3 className="font-bold tracking-tight text-xl mb-4 flex items-center gap-2">
            <CheckCircle2 className="w-6 h-6" /> Top strengths
          </h3>
          {results.strengths?.length ? (
            <ul className="space-y-2">
              {results.strengths.map((s, i) => (
                <li key={i} className="font-medium flex gap-2 justify-between">
                  <span className="flex gap-2">
                    <span className="font-bold">✓</span> {s.category || s}
                  </span>
                  {typeof s.percent === 'number' && (
                    <span className="font-bold">{s.percent}%</span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="font-medium text-gray-600">
              No standout strengths yet — a great reason to dig deeper.
            </p>
          )}
        </div>
        <div className="border border-ink/10 rounded-lg shadow-saas bg-white p-6">
          <h3 className="font-bold tracking-tight text-xl mb-4 flex items-center gap-2">
            <AlertTriangle className="w-6 h-6" /> Biggest gaps
          </h3>
          {results.gaps?.length ? (
            <ul className="space-y-2">
              {results.gaps.map((g, i) => (
                <li key={i} className="font-medium flex gap-2 justify-between">
                  <span className="flex gap-2">
                    <span className="font-bold">!</span> {g.category || g}
                  </span>
                  {typeof g.percent === 'number' && (
                    <span className="font-bold">{g.percent}%</span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="font-medium text-gray-600">No major gaps detected.</p>
          )}
        </div>
      </div>

      {/* Priority areas */}
      {results.priority_areas?.length > 0 && (
        <div className="mb-10">
          <h2 className="font-bold tracking-tight text-2xl mb-4 flex items-center gap-2">
            <Target className="w-6 h-6" /> Where to focus first
          </h2>
          <div className="space-y-4">
            {results.priority_areas.map((p, i) => (
              <div
                key={i}
                className="border border-ink/10 rounded-lg bg-lime/30 p-5 flex gap-4"
              >
                <div className="text-3xl font-extrabold">{i + 1}</div>
                <div>
                  <div className="font-bold text-lg">{p.category}</div>
                  {p.recommendation && (
                    <p className="font-medium mt-1">{p.recommendation}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Optional AI summary */}
      {ai_summary && (
        <div className="border border-ink/10 rounded-lg shadow-saas bg-ink text-white p-6 mb-10">
          <h3 className="font-bold tracking-tight text-xl mb-4 flex items-center gap-2 text-lime">
            <Sparkles className="w-6 h-6" /> Adviser summary
          </h3>
          {ai_summary.maturity_summary && (
            <p className="font-medium mb-4">{ai_summary.maturity_summary}</p>
          )}
          {ai_summary.next_steps?.length > 0 && (
            <div>
              <div className="font-bold uppercase text-sm mb-2 text-lime">
                Suggested next steps
              </div>
              <ul className="space-y-1">
                {ai_summary.next_steps.map((s, i) => (
                  <li key={i} className="font-medium flex gap-2">
                    <ArrowRight className="w-5 h-5 flex-shrink-0 mt-0.5" /> {s}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* CTA into full review */}
      <div className="border border-ink/10 rounded-lg shadow-saas bg-red-500 text-white p-8 text-center">
        <h2 className="font-bold tracking-tight text-3xl mb-3">Ready to go deeper?</h2>
        <p className="font-medium text-lg mb-6 max-w-2xl mx-auto">
          This snapshot scratches the surface. A full GovernWell review takes your
          answers further — with evidence and a board-ready improvement
          plan.
        </p>
        <button
          onClick={onStartFullReview}
          className="bg-ink text-white border border-ink/10 rounded-lg shadow-saas px-8 py-4 font-bold text-xl inline-flex items-center gap-3"
        >
          Start my full review <ArrowRight className="w-6 h-6" />
        </button>
      </div>
    </div>
  );
};

export default HealthCheck;
